/**
 * InOut Service — the person's record of residence absences, their clocks and
 * plans (InOut plan §8, Lane 6). Always the caller's own record: no method
 * takes a talent or user id. Every number is the engine's (BRAIN); the client
 * renders it and never computes a day.
 */
import type {
  InOutBoardingPassFlight,
  InOutBoardingPassTrip,
  InOutEvaluation,
} from '@tonnie/heroes';
import type {
  InOutAvailabilityQuery,
  InOutConsentGrantInput,
  InOutConsultationCheckoutInput,
  InOutConsultationSlotInput,
  InOutRefundRequestInput,
  InOutRequisitionCheckoutInput,
  InOutCaseEventInput,
  InOutTrackingFileInput,
  InOutCalculatorEvaluateInput,
  InOutCalculatorSimulateInput,
  InOutKeptCalculationInput,
  InOutHandoffCreateInput,
  InOutPortalSettingsInput,
  InOutConfirmationAnswerInput,
  InOutNoticePreferencesInput,
  InOutAbsenceCreateInput,
  InOutAbsenceJoinInput,
  InOutAbsenceUpdateInput,
  InOutEvidenceAttachInput,
  InOutPlannedTripCreateInput,
  InOutPlannedTripUpdateInput,
  InOutSimulateInput,
  InOutTitleCreateInput,
  InOutTitleUpdateInput,
} from '@tonnie/schemas';
import type {
  DataSharingConsentView,
  InOutAgencyAnswer,
  InOutCaseView,
  InOutCheckoutAnswer,
  InOutConsultationAvailability,
  InOutConsultationView,
  InOutContactRequestAnswer,
  InOutRefundRequestView,
  InOutOperatorCaseDetail,
  InOutOperatorCaseRow,
  InOutReconciliationView,
  InOutTrackingImportView,
  InOutTrackingVocabularyView,
  InOutAbsence,
  InOutAbsenceCreated,
  InOutAbsenceEvidence,
  InOutEvidenceVocabulary,
  InOutNoticePreferences,
  InOutKeptCalculationOutcome,
  InOutHandoffCreated,
  InOutHandoffSummary,
  InOutOperatorPartner,
  InOutPortalAdminView,
  InOutPortalView,
  InOutRecordDetail as InOutRecordDetailOf,
  InOutRecordSummary,
  InOutRecordView,
  InOutRegimeOption,
  InOutPlannedTrip,
  InOutPresenceConfirmation,
  InOutReading as InOutReadingOf,
  InOutTripReading as InOutTripReadingOf,
  InOutTitle,
} from '@tonnie/types';

import { TonnieClient, type BlobWithFilename } from '../client/base';

/**
 * The clocks and the rules' words beside them (the regime's name, the
 * exceptions) — what the dashboard reads, and the public calculator answers (AC-1).
 */
export type InOutReading = InOutReadingOf<InOutEvaluation>;

/** A trip simulated on a record, read on its return day — never saved. */
export type InOutTripReading = InOutTripReadingOf<InOutEvaluation>;

/** One InOut of the person's history, read whole — a closed one's clocks as of the day it was closed (H7). */
export type InOutRecordDetail = InOutRecordDetailOf<InOutEvaluation>;

/** No InOut running — none yet (`deactivatedAt: null`), or one paused since that moment. */
export interface InOutNotRunning {
  configured: false;
  deactivatedAt: string | null;
}

/** The clocks — or no InOut running (nothing to count against). */
export type InOutStatusAnswer = InOutNotRunning | ({ configured: true } & InOutReading);

/** A trip simulated on the person's record — or no InOut running. */
export type InOutSimulationAnswer = InOutNotRunning | ({ configured: true } & InOutTripReading);

/** The regimes a country offers, in the viewer's language — for the public calculator. */
export interface InOutCalculatorRegimes {
  countryCode: string;
  configVersion: string;
  regimes: InOutRegimeOption[];
}

const BASE = '/api/inout';

/** A boarding pass read: the flight, and the trip it is on the person's record (6.1). */
export interface InOutBoardingPassAnswer {
  flight: InOutBoardingPassFlight;
  trip: InOutBoardingPassTrip;
}

export class InOutService {
  constructor(private client: TonnieClient) {}

  // ── The public calculator (before sign-up; nothing saved) ──
  calculatorRegimes(countryCode: string): Promise<InOutCalculatorRegimes> {
    return this.client.get<InOutCalculatorRegimes>(
      `${BASE}/calculator/regimes?country=${encodeURIComponent(countryCode)}`
    );
  }
  /** The dashboard's engine on a record the person describes (AC-1). */
  calculatorEvaluate(input: InOutCalculatorEvaluateInput): Promise<InOutReading> {
    return this.client.post<InOutReading>(`${BASE}/calculator/evaluate`, input);
  }
  /** "What if I travel?" — one trip on the described record, never saved. */
  calculatorSimulate(input: InOutCalculatorSimulateInput): Promise<InOutTripReading> {
    return this.client.post<InOutTripReading>(`${BASE}/calculator/simulate`, input);
  }

  // ── The calculator on a portal, and its handoff (before sign-up) ──
  /** An active portal as its calculator reads it. */
  getEmbedPortal(code: string): Promise<InOutPortalView> {
    return this.client.get<InOutPortalView>(`${BASE}/embed/portals/${encodeURIComponent(code)}`);
  }
  /** A kept calculation handed to Tonnie — a one-time token and the URL that continues. */
  createHandoff(input: InOutHandoffCreateInput): Promise<InOutHandoffCreated> {
    return this.client.post<InOutHandoffCreated>(`${BASE}/calculator/handoff`, input);
  }
  /** What sign-up says back — the portal and two counts. */
  getHandoffSummary(token: string): Promise<InOutHandoffSummary> {
    return this.client.get<InOutHandoffSummary>(
      `${BASE}/calculator/handoff/${encodeURIComponent(token)}`
    );
  }
  /** Signed in: the handed-off calculation becomes the record. */
  adoptHandoff(token: string): Promise<InOutKeptCalculationOutcome> {
    return this.client.post<InOutKeptCalculationOutcome>(`${BASE}/setup/handoff`, { token });
  }
  /** An InOut already there: a new one from the handed-off calculation, attributed to its portal — the one there goes to the history. */
  replaceWithHandoff(token: string): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(`${BASE}/setup/handoff/replace`, { token });
  }

  // ── The InOut as a whole ──
  /** none | active | deactivated, with what it holds — no engine. */
  getRecord(): Promise<InOutRecordView> {
    return this.client.get<InOutRecordView>(`${BASE}/record`);
  }
  /** Pause it — kept as it is, read by nobody until it is reactivated. */
  deactivateRecord(): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(`${BASE}/record/deactivate`, {});
  }
  reactivateRecord(): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(`${BASE}/record/reactivate`, {});
  }
  /** Close it into the history, with no new one (H2). */
  closeRecord(): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(`${BASE}/record/close`, {});
  }

  // ── The history ──
  /** Every InOut — the principal first, then the closed ones. */
  listRecords(): Promise<InOutRecordSummary[]> {
    return this.client.get<InOutRecordSummary[]>(`${BASE}/records`);
  }
  /** One InOut whole — a closed one's clocks as of the day it was closed (H7). */
  getRecordDetail(id: string): Promise<InOutRecordDetail> {
    return this.client.get<InOutRecordDetail>(`${BASE}/records/${encodeURIComponent(id)}`);
  }
  /** A new InOut from a calculation — the principal, if any, goes to the history (nothing is deleted). */
  newRecord(input: InOutKeptCalculationInput): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(`${BASE}/records/new`, input);
  }
  /** A closed InOut back as the principal — paused, for the person to resume (H1). */
  restoreRecord(id: string): Promise<InOutRecordView> {
    return this.client.post<InOutRecordView>(
      `${BASE}/records/${encodeURIComponent(id)}/restore`,
      {}
    );
  }
  /** A closed InOut deleted for good — never one a case with the agency was opened on (H3). */
  deleteRecord(id: string): Promise<void> {
    return this.client.delete<void>(`${BASE}/records/${encodeURIComponent(id)}`);
  }

  // ── Titles ──
  listTitles(): Promise<InOutTitle[]> {
    return this.client.get<InOutTitle[]>(`${BASE}/titles`);
  }
  createTitle(input: InOutTitleCreateInput): Promise<InOutTitle> {
    return this.client.post<InOutTitle>(`${BASE}/titles`, input);
  }
  updateTitle(id: string, input: InOutTitleUpdateInput): Promise<InOutTitle> {
    return this.client.patch<InOutTitle>(`${BASE}/titles/${id}`, input);
  }
  removeTitle(id: string): Promise<void> {
    return this.client.delete<void>(`${BASE}/titles/${id}`);
  }

  // ── Absences ──
  listAbsences(): Promise<InOutAbsence[]> {
    return this.client.get<InOutAbsence[]>(`${BASE}/absences`);
  }
  /** No dates: the In/Out switch (away from today). With dates: one written down. */
  /** No dates: the switch — out again the day they came back, that trip continues (`continued`). */
  createAbsence(input: InOutAbsenceCreateInput = {}): Promise<InOutAbsenceCreated> {
    return this.client.post<InOutAbsenceCreated>(`${BASE}/absences`, input);
  }
  /** A trip that shares only a border day with absence `id`, made one with it. */
  joinAbsence(id: string, input: InOutAbsenceJoinInput): Promise<InOutAbsence> {
    return this.client.post<InOutAbsence>(`${BASE}/absences/${id}/join`, input);
  }
  updateAbsence(id: string, input: InOutAbsenceUpdateInput): Promise<InOutAbsence> {
    return this.client.patch<InOutAbsence>(`${BASE}/absences/${id}`, input);
  }
  /** The switch's second touch: back today (the server's day for the person). */
  returnFromAbsence(id: string): Promise<InOutAbsence> {
    return this.client.post<InOutAbsence>(`${BASE}/absences/${id}/return`, {});
  }
  removeAbsence(id: string): Promise<void> {
    return this.client.delete<void>(`${BASE}/absences/${id}`);
  }
  attachEvidence(
    absenceId: string,
    input: InOutEvidenceAttachInput
  ): Promise<InOutAbsenceEvidence> {
    return this.client.post<InOutAbsenceEvidence>(`${BASE}/absences/${absenceId}/evidence`, input);
  }
  detachEvidence(absenceId: string, evidenceId: string): Promise<void> {
    return this.client.delete<void>(`${BASE}/absences/${absenceId}/evidence/${evidenceId}`);
  }
  /** A boarding pass in the Vault, read into the trip it is — a proposal; nothing is written. */
  readBoardingPass(vaultFileId: string): Promise<InOutBoardingPassAnswer> {
    return this.client.post<InOutBoardingPassAnswer>(`${BASE}/boarding-pass`, { vaultFileId });
  }

  // ── Clocks and plans ──
  getStatus(): Promise<InOutStatusAnswer> {
    return this.client.get<InOutStatusAnswer>(`${BASE}/status`);
  }
  simulate(input: InOutSimulateInput): Promise<InOutSimulationAnswer> {
    return this.client.post<InOutSimulationAnswer>(`${BASE}/simulate`, input);
  }
  listPlannedTrips(): Promise<InOutPlannedTrip[]> {
    return this.client.get<InOutPlannedTrip[]>(`${BASE}/planned-trips`);
  }
  createPlannedTrip(input: InOutPlannedTripCreateInput): Promise<InOutPlannedTrip> {
    return this.client.post<InOutPlannedTrip>(`${BASE}/planned-trips`, input);
  }
  updatePlannedTrip(id: string, input: InOutPlannedTripUpdateInput): Promise<InOutPlannedTrip> {
    return this.client.patch<InOutPlannedTrip>(`${BASE}/planned-trips/${id}`, input);
  }
  removePlannedTrip(id: string): Promise<void> {
    return this.client.delete<void>(`${BASE}/planned-trips/${id}`);
  }

  /** What the record's country accepts as proof of a trip. */
  getEvidenceVocabulary(): Promise<InOutEvidenceVocabulary> {
    return this.client.get<InOutEvidenceVocabulary>(`${BASE}/evidence/vocabulary`);
  }

  // ── Setup ──
  /** A calculation made in InOut's calculator becomes the record. */
  adoptKeptCalculation(input: InOutKeptCalculationInput): Promise<InOutKeptCalculationOutcome> {
    return this.client.post<InOutKeptCalculationOutcome>(`${BASE}/setup/calculation`, input);
  }

  // ── Notices ──
  getNoticePreferences(): Promise<InOutNoticePreferences> {
    return this.client.get<InOutNoticePreferences>(`${BASE}/notices/preferences`);
  }
  putNoticePreferences(input: InOutNoticePreferencesInput): Promise<InOutNoticePreferences> {
    return this.client.put<InOutNoticePreferences>(`${BASE}/notices/preferences`, input);
  }
  listConfirmations(): Promise<InOutPresenceConfirmation[]> {
    return this.client.get<InOutPresenceConfirmation[]>(`${BASE}/notices/confirmations`);
  }
  answerConfirmation(
    id: string,
    input: InOutConfirmationAnswerInput
  ): Promise<InOutPresenceConfirmation> {
    return this.client.post<InOutPresenceConfirmation>(
      `${BASE}/notices/confirmations/${id}`,
      input
    );
  }

  // ── The dossier ──
  /**
   * The agency's copy of a dossier, through the private link it was emailed —
   * public: the token is the credential. A dead link (unknown, expired,
   * revoked, its consent withdrawn) is a 404, whatever the reason.
   */
  // ── The agency (Lane 8) ──
  /** The agency of the record's country, its purposes, disclaimer and prices — or not ready yet. */
  getAgency(): Promise<InOutAgencyAnswer> {
    return this.client.get<InOutAgencyAnswer>(`${BASE}/agency`);
  }
  /** Approve a purpose's sharing with the agency (D2). */
  grantConsent(input: InOutConsentGrantInput): Promise<DataSharingConsentView> {
    return this.client.post<DataSharingConsentView>(`${BASE}/consents`, input);
  }
  /** The person's consents to share — every partner's, standing and revoked (the platform's list). */
  listDataSharingConsents(): Promise<DataSharingConsentView[]> {
    return this.client.get<DataSharingConsentView[]>('/api/consents/data-sharing');
  }
  revokeDataSharingConsent(id: string): Promise<DataSharingConsentView> {
    return this.client.delete<DataSharingConsentView>(`/api/consents/data-sharing/${id}`);
  }
  getConsultationAvailability(
    range: InOutAvailabilityQuery
  ): Promise<InOutConsultationAvailability> {
    return this.client.get<InOutConsultationAvailability>(`${BASE}/consultation/availability`, {
      params: range,
    });
  }
  requestContact(): Promise<InOutContactRequestAnswer> {
    return this.client.post<InOutContactRequestAnswer>(`${BASE}/consultation/contact-request`, {});
  }
  checkoutConsultation(input: InOutConsultationCheckoutInput): Promise<InOutCheckoutAnswer> {
    return this.client.post<InOutCheckoutAnswer>(`${BASE}/consultation/checkout`, input);
  }
  moveConsultation(id: string, input: InOutConsultationSlotInput): Promise<InOutConsultationView> {
    return this.client.patch<InOutConsultationView>(`${BASE}/consultation/${id}/slot`, input);
  }
  listCases(): Promise<InOutCaseView[]> {
    return this.client.get<InOutCaseView[]>(`${BASE}/cases`);
  }
  checkoutRequisition(input: InOutRequisitionCheckoutInput): Promise<InOutCheckoutAnswer> {
    return this.client.post<InOutCheckoutAnswer>(`${BASE}/requisition/checkout`, input);
  }
  requestRefund(caseId: string, input: InOutRefundRequestInput): Promise<InOutRefundRequestView> {
    return this.client.post<InOutRefundRequestView>(
      `${BASE}/cases/${caseId}/refund-request`,
      input
    );
  }
  /** The person's own copy of their paid requisition's dossier. */
  getOwnDossier(): Promise<BlobWithFilename> {
    return this.client.get<BlobWithFilename>(`${BASE}/dossier`, {
      responseType: 'blob',
      withFilename: true,
    });
  }
  /** The person's own record, ready for an audit (PDF) — no case, nothing bought. */
  getOwnRecord(): Promise<BlobWithFilename> {
    return this.client.get<BlobWithFilename>(`${BASE}/dossier/record`, {
      responseType: 'blob',
      withFilename: true,
    });
  }

  // ── The operation (Tonnie staff) ──
  listPortals(): Promise<InOutPortalAdminView[]> {
    return this.client.get<InOutPortalAdminView[]>(`${BASE}/operator/portals`);
  }
  listOperatorPartners(): Promise<InOutOperatorPartner[]> {
    return this.client.get<InOutOperatorPartner[]>(`${BASE}/operator/partners`);
  }
  savePortal(code: string, input: InOutPortalSettingsInput): Promise<InOutPortalAdminView> {
    return this.client.put<InOutPortalAdminView>(
      `${BASE}/operator/portals/${encodeURIComponent(code)}`,
      input
    );
  }
  listOperatorCases(): Promise<InOutOperatorCaseRow[]> {
    return this.client.get<InOutOperatorCaseRow[]>(`${BASE}/operator/cases`);
  }
  getOperatorCase(id: string): Promise<InOutOperatorCaseDetail> {
    return this.client.get<InOutOperatorCaseDetail>(`${BASE}/operator/cases/${id}`);
  }
  recordCaseEvent(id: string, input: InOutCaseEventInput): Promise<InOutOperatorCaseDetail> {
    return this.client.post<InOutOperatorCaseDetail>(`${BASE}/operator/cases/${id}/events`, input);
  }
  handleRefundRequest(caseId: string, requestId: string): Promise<InOutOperatorCaseDetail> {
    return this.client.post<InOutOperatorCaseDetail>(
      `${BASE}/operator/cases/${caseId}/refund-requests/${requestId}/handled`,
      {}
    );
  }
  getTrackingVocabulary(agencyCode: string): Promise<InOutTrackingVocabularyView> {
    return this.client.get<InOutTrackingVocabularyView>(`${BASE}/operator/vocabulary`, {
      params: { agencyCode },
    });
  }
  previewTrackingFile(input: InOutTrackingFileInput): Promise<InOutTrackingImportView> {
    return this.client.post<InOutTrackingImportView>(`${BASE}/operator/imports`, input);
  }
  commitTrackingFile(id: string): Promise<InOutTrackingImportView> {
    return this.client.post<InOutTrackingImportView>(`${BASE}/operator/imports/${id}/commit`, {});
  }
  listTrackingFiles(): Promise<InOutTrackingImportView[]> {
    return this.client.get<InOutTrackingImportView[]>(`${BASE}/operator/imports`);
  }
  getReconciliation(month: string): Promise<InOutReconciliationView> {
    return this.client.get<InOutReconciliationView>(`${BASE}/operator/reconciliation`, {
      params: { month },
    });
  }

  getSharedDossier(token: string): Promise<BlobWithFilename> {
    return this.client.get<BlobWithFilename>(
      `${BASE}/dossier/shared/${encodeURIComponent(token)}`,
      { responseType: 'blob', withFilename: true }
    );
  }
}

export function createInOutService(client: TonnieClient): InOutService {
  return new InOutService(client);
}
